import { useState, useRef, useCallback } from "preact/hooks";
import { formatDate } from "../../lib/dateUtils";
import { useEdition } from "../../hooks/useEdition";
import { useTranslations } from "../../hooks/useTranslations";

type DaySelectorProps = {
  days: string[];
  selectedDay: string | null;
  onChange: (day: string | null) => void;
  showAll?: boolean;
  className?: string;
  dropdownThreshold?: number;
};

export function DaySelector({
  days,
  selectedDay,
  onChange,
  showAll = true,
  className = "",
  dropdownThreshold = 7,
}: DaySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { locale } = useEdition();
  const t = useTranslations();

  const handleBackdropClick = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  // Close on outside click
  if (typeof document !== "undefined") {
    if (isOpen) {
      document.addEventListener("click", handleBackdropClick);
    } else {
      document.removeEventListener("click", handleBackdropClick);
    }
  }

  if (days.length === 0) return null;

  const useDropdown = days.length >= dropdownThreshold;

  // Dropdown mode for many days
  if (useDropdown) {
    const selectedLabel = selectedDay
      ? formatDate(selectedDay, "ddd L", locale)
      : t.allDays;

    return (
      <div className={`festapp-day-dropdown ${className}`} ref={dropdownRef}>
        <button
          type="button"
          className={`festapp-day-dropdown-trigger ${isOpen ? "festapp-day-dropdown-trigger-active" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="festapp-day-dropdown-trigger-content">
            <span className="festapp-day-dropdown-label">{t.date}</span>
            <span className="festapp-day-dropdown-value">{selectedLabel}</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`festapp-day-dropdown-chevron ${isOpen ? "festapp-day-dropdown-chevron-open" : ""}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {isOpen && (
          <div className="festapp-day-dropdown-menu">
            {showAll && (
              <button
                type="button"
                className={`festapp-day-dropdown-item ${selectedDay === null ? "festapp-day-dropdown-item-active" : ""}`}
                onClick={() => {
                  onChange(null);
                  setIsOpen(false);
                }}
              >
                {t.allDays}
              </button>
            )}
            {days.map((day) => (
              <button
                key={day}
                type="button"
                className={`festapp-day-dropdown-item ${selectedDay === day ? "festapp-day-dropdown-item-active" : ""}`}
                onClick={() => {
                  onChange(day);
                  setIsOpen(false);
                }}
              >
                {formatDate(day, "dddd, LL", locale)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Regular pill mode for fewer days
  return (
    <div className={`festapp-day-selector ${className}`}>
      {showAll && (
        <button
          className={`festapp-day-btn ${selectedDay === null ? "festapp-day-btn-active" : ""}`}
          onClick={() => onChange(null)}
        >
          {t.all}
        </button>
      )}
      {days.map((day) => (
        <button
          key={day}
          className={`festapp-day-btn ${selectedDay === day ? "festapp-day-btn-active" : ""}`}
          onClick={() => onChange(day)}
        >
          {formatDate(day, "ddd L", locale)}
        </button>
      ))}
    </div>
  );
}
